import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { IconArrowLeft, IconUpload, IconMap, IconRoute, IconMountain, IconRuler, IconPlayerPlay, IconPlayerPause, IconPlayerStop, IconSatellite, IconTrees, IconPhoto, IconEye, Icon3dCubeSphere } from '@tabler/icons-react';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage, functions } from '../firebase';
import { useAdminView } from '../contexts/AdminViewContext';
import 'mapbox-gl/dist/mapbox-gl.css';
import './GpxViewerPage.css';

// Mapbox access token
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';

if (typeof mapboxgl !== 'undefined') {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

// Default sample GPX data (Morning Ride from Downloads)
const DEFAULT_GPX_URL = '/sample-route.gpx';

// Default animation speed in km/h
const DEFAULT_ANIMATION_SPEED_KMH = 100;

// Animation zoom level (higher = more zoomed in)
const ANIMATION_ZOOM_LEVEL = 13;

// Camera pitch for 3D terrain view (0 = looking straight down, 60 = angled view)
const CAMERA_PITCH = 60;

// Bearing offset to look slightly behind the puck (positive = look back at where we came from)
const BEARING_OFFSET = 0; // Look in the direction of travel

// Default camera settings for FreeCamera API (fixed altitude prevents flickering over terrain)
const DEFAULT_CAMERA_ALTITUDE = 800;
const DEFAULT_CAMERA_DISTANCE_BEHIND = 1600;

/**
 * Calculate a position offset from a point in a given direction
 * @param {[number, number]} position - [lng, lat]
 * @param {number} bearing - Direction in degrees (0 = north, 90 = east)
 * @param {number} distance - Distance in meters
 * @returns {[number, number]} - New [lng, lat] position
 */
const offsetPosition = (position, bearing, distance) => {
  const R = 6371000; // Earth's radius in meters
  const bearingRad = (bearing * Math.PI) / 180;
  const lat1 = (position[1] * Math.PI) / 180;
  const lng1 = (position[0] * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distance / R) +
    Math.cos(lat1) * Math.sin(distance / R) * Math.cos(bearingRad)
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(bearingRad) * Math.sin(distance / R) * Math.cos(lat1),
    Math.cos(distance / R) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
};

/**
 * Parse GPX XML and extract coordinates with elevation
 */
const parseGPX = (gpxString) => {
  const parser = new DOMParser();
  const gpxDoc = parser.parseFromString(gpxString, 'text/xml');

  // Check for parse errors
  const parseError = gpxDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Invalid GPX file format');
  }

  const coordinates = [];
  const elevations = [];

  // Try to get track points first
  let trackPoints = gpxDoc.querySelectorAll('trkpt');

  // If no track points, try route points
  if (trackPoints.length === 0) {
    trackPoints = gpxDoc.querySelectorAll('rtept');
  }

  // If still no points, try waypoints
  if (trackPoints.length === 0) {
    trackPoints = gpxDoc.querySelectorAll('wpt');
  }

  trackPoints.forEach((point) => {
    const lat = parseFloat(point.getAttribute('lat'));
    const lon = parseFloat(point.getAttribute('lon'));
    const eleElement = point.querySelector('ele');
    const elevation = eleElement ? parseFloat(eleElement.textContent) : 0;

    if (!isNaN(lat) && !isNaN(lon)) {
      coordinates.push([lon, lat]);
      elevations.push(elevation);
    }
  });

  // Extract metadata if available
  const nameElement = gpxDoc.querySelector('trk > name') || gpxDoc.querySelector('metadata > name');
  const name = nameElement ? nameElement.textContent : 'GPX Route';

  return {
    coordinates,
    elevations,
    name,
    pointCount: coordinates.length
  };
};

/**
 * Calculate route statistics
 */
const calculateStats = (coordinates, elevations) => {
  if (coordinates.length < 2) {
    return { distance: 0, elevationGain: 0, elevationLoss: 0, maxElevation: 0, minElevation: 0 };
  }

  let distance = 0;
  let elevationGain = 0;
  let elevationLoss = 0;

  for (let i = 1; i < coordinates.length; i++) {
    // Calculate distance using Haversine formula
    const [lon1, lat1] = coordinates[i - 1];
    const [lon2, lat2] = coordinates[i];

    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    distance += R * c;

    // Calculate elevation changes
    if (elevations[i] !== undefined && elevations[i - 1] !== undefined) {
      const elevDiff = elevations[i] - elevations[i - 1];
      if (elevDiff > 0) {
        elevationGain += elevDiff;
      } else {
        elevationLoss += Math.abs(elevDiff);
      }
    }
  }

  const validElevations = elevations.filter(e => !isNaN(e) && e !== undefined);
  const maxElevation = validElevations.length > 0 ? Math.max(...validElevations) : 0;
  const minElevation = validElevations.length > 0 ? Math.min(...validElevations) : 0;

  return {
    distance: distance.toFixed(1),
    distanceMeters: distance * 1000,
    elevationGain: Math.round(elevationGain),
    elevationLoss: Math.round(elevationLoss),
    maxElevation: Math.round(maxElevation),
    minElevation: Math.round(minElevation)
  };
};

/**
 * Calculate cumulative distances for each point
 */
const calculateCumulativeDistances = (coordinates) => {
  const distances = [0];
  let totalDistance = 0;

  for (let i = 1; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i - 1];
    const [lon2, lat2] = coordinates[i];

    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    totalDistance += R * c;
    distances.push(totalDistance);
  }

  return distances;
};

/**
 * Calculate bearing (heading) between two points in degrees
 * Returns 0 for north, 90 for east, 180 for south, 270 for west
 */
const calculateBearing = (lon1, lat1, lon2, lat2) => {
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
            Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360; // Normalize to 0-360
};

/**
 * Interpolate position along route at a given distance
 */
const getPositionAtDistance = (coordinates, cumulativeDistances, targetDistance) => {
  if (targetDistance <= 0) return coordinates[0];
  if (targetDistance >= cumulativeDistances[cumulativeDistances.length - 1]) {
    return coordinates[coordinates.length - 1];
  }

  // Find the segment containing the target distance
  let segmentIndex = 0;
  for (let i = 1; i < cumulativeDistances.length; i++) {
    if (cumulativeDistances[i] >= targetDistance) {
      segmentIndex = i - 1;
      break;
    }
  }

  // Interpolate within the segment
  const segmentStart = cumulativeDistances[segmentIndex];
  const segmentEnd = cumulativeDistances[segmentIndex + 1];
  const segmentLength = segmentEnd - segmentStart;
  const distanceIntoSegment = targetDistance - segmentStart;
  const t = segmentLength > 0 ? distanceIntoSegment / segmentLength : 0;

  const [lon1, lat1] = coordinates[segmentIndex];
  const [lon2, lat2] = coordinates[segmentIndex + 1];

  return [
    lon1 + (lon2 - lon1) * t,
    lat1 + (lat2 - lat1) * t
  ];
};

/**
 * Get bearing at a specific distance along the route
 * Looks ahead a small distance to determine direction of travel
 */
const getBearingAtDistance = (coordinates, cumulativeDistances, targetDistance) => {
  const totalDistance = cumulativeDistances[cumulativeDistances.length - 1];

  // Look ahead by a small amount to get direction
  const lookAhead = Math.min(50, totalDistance - targetDistance); // 50 meters ahead
  const currentPos = getPositionAtDistance(coordinates, cumulativeDistances, targetDistance);
  const aheadPos = getPositionAtDistance(coordinates, cumulativeDistances, Math.min(targetDistance + lookAhead, totalDistance));

  return calculateBearing(currentPos[0], currentPos[1], aheadPos[0], aheadPos[1]);
};

// Drag sensitivity: how many meters per pixel of vertical drag
const DRAG_METERS_PER_PIXEL = 50;

// Tile processing zoom level
const TILE_PROCESSING_ZOOM = 13;

/**
 * Convert longitude to tile X coordinate at a given zoom level
 */
const lngToTileX = (lng, zoom) => {
  return Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
};

/**
 * Convert latitude to tile Y coordinate at a given zoom level
 */
const latToTileY = (lat, zoom) => {
  const latRad = lat * Math.PI / 180;
  return Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, zoom));
};

/**
 * Get all unique tiles that a route passes through at a given zoom level
 */
const getRouteTiles = (coordinates, zoom) => {
  const tileSet = new Set();

  coordinates.forEach(([lng, lat]) => {
    const x = lngToTileX(lng, zoom);
    const y = latToTileY(lat, zoom);
    tileSet.add(`${zoom}/${x}/${y}`);
  });

  return Array.from(tileSet).map(key => {
    const [z, x, y] = key.split('/').map(Number);
    return { z, x, y, key };
  });
};

/**
 * Apply edge detection (Sobel filter) to an image
 */
const applyEdgeDetection = (imageData) => {
  const { data, width, height } = imageData;
  const output = new Uint8ClampedArray(data.length);

  // Sobel kernels
  const sobelX = [[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]];
  const sobelY = [[-1, -2, -1], [0, 0, 0], [1, 2, 1]];

  // Convert to grayscale first
  const gray = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const idx = i * 4;
    gray[i] = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
  }

  // Apply Sobel filter
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let gx = 0, gy = 0;

      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const idx = (y + ky) * width + (x + kx);
          gx += gray[idx] * sobelX[ky + 1][kx + 1];
          gy += gray[idx] * sobelY[ky + 1][kx + 1];
        }
      }

      const magnitude = Math.min(255, Math.sqrt(gx * gx + gy * gy));
      const outIdx = (y * width + x) * 4;

      // White edges on dark background
      output[outIdx] = magnitude;
      output[outIdx + 1] = magnitude;
      output[outIdx + 2] = magnitude;
      output[outIdx + 3] = 255;
    }
  }

  return new ImageData(output, width, height);
};

/**
 * Apply grayscale effect to an image
 */
const applyGrayscale = (imageData) => {
  const { data, width, height } = imageData;
  const output = new Uint8ClampedArray(data.length);

  for (let i = 0; i < width * height; i++) {
    const idx = i * 4;
    const gray = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
    output[idx] = gray;
    output[idx + 1] = gray;
    output[idx + 2] = gray;
    output[idx + 3] = data[idx + 3]; // Keep original alpha
  }

  return new ImageData(output, width, height);
};

/**
 * Apply high contrast effect to an image
 */
const applyHighContrast = (imageData) => {
  const { data, width, height } = imageData;
  const output = new Uint8ClampedArray(data.length);
  const contrastFactor = 1.8; // Increase contrast

  for (let i = 0; i < width * height; i++) {
    const idx = i * 4;
    // Apply contrast enhancement
    for (let c = 0; c < 3; c++) {
      const value = data[idx + c];
      const adjusted = Math.round((value - 128) * contrastFactor + 128);
      output[idx + c] = Math.max(0, Math.min(255, adjusted));
    }
    output[idx + 3] = data[idx + 3]; // Keep original alpha
  }

  return new ImageData(output, width, height);
};

/**
 * Apply processing based on style key
 */
const applyProcessing = (imageData, styleKey) => {
  switch (styleKey) {
    case 'grayscale':
      return applyGrayscale(imageData);
    case 'high-contrast':
      return applyHighContrast(imageData);
    case 'edge-detection':
    default:
      return applyEdgeDetection(imageData);
  }
};

/**
 * Upload a blob to Firebase Storage
 * File structure: tiles/input/{z}_{x}_{y}.jpg or tiles/processed/{styleKey}/{z}_{x}_{y}.png
 */
const uploadTileToStorage = async (blob, path) => {
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob);
    const url = await getDownloadURL(storageRef);
    console.log(`Uploaded tile to ${path}`);
    return url;
  } catch (error) {
    console.error(`Failed to upload tile to ${path}:`, error);
    throw error;
  }
};

/**
 * Check if a processed tile exists in Firebase Storage and return its URL
 */
const getExistingTileUrl = async (z, x, y, styleKey) => {
  try {
    const tilePath = `tiles/processed/${styleKey}/${z}_${x}_${y}.png`;
    const storageRef = ref(storage, tilePath);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    // Tile doesn't exist in storage
    return null;
  }
};

/**
 * Fetch and process a single tile, optionally saving input and output to Firebase Storage
 * First checks if a processed tile already exists in Firebase Storage
 * File structure: tiles/input/{z}_{x}_{y}.jpg and tiles/processed/{styleKey}/{z}_{x}_{y}.png
 */
const fetchAndProcessTile = async (z, x, y, accessToken, styleKey = 'edge-detection', saveTiles = false) => {
  // First, check if processed tile already exists in Firebase Storage
  const existingUrl = await getExistingTileUrl(z, x, y, styleKey);
  if (existingUrl) {
    console.log(`Using cached tile from Firebase: ${z}_${x}_${y} (${styleKey})`);
    return { z, x, y, blobUrl: existingUrl, fromCache: true };
  }

  // If not cached, process the tile locally
  const tileUrl = `https://api.mapbox.com/v4/mapbox.satellite/${z}/${x}/${y}@2x.jpg90?access_token=${accessToken}`;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(img, 0, 0);

      // Save input tile to Firebase Storage if requested
      if (saveTiles) {
        canvas.toBlob(async (inputBlob) => {
          if (inputBlob) {
            const inputPath = `tiles/input/${z}_${x}_${y}.jpg`;
            try {
              await uploadTileToStorage(inputBlob, inputPath);
            } catch (err) {
              console.warn('Failed to upload input tile:', err);
            }
          }
        }, 'image/jpeg', 0.9);
      }

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Apply processing based on style
      const processedData = applyProcessing(imageData, styleKey);
      ctx.putImageData(processedData, 0, 0);

      // Save output tile to Firebase Storage if requested
      if (saveTiles) {
        canvas.toBlob(async (outputBlob) => {
          if (outputBlob) {
            const outputPath = `tiles/processed/${styleKey}/${z}_${x}_${y}.png`;
            try {
              await uploadTileToStorage(outputBlob, outputPath);
            } catch (err) {
              console.warn('Failed to upload output tile:', err);
            }
          }
        }, 'image/png');
      }

      // Convert to blob URL
      canvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          resolve({ z, x, y, blobUrl, fromCache: false });
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/png');
    };

    img.onerror = () => reject(new Error(`Failed to load tile ${z}/${x}/${y}`));
    img.src = tileUrl;
  });
};

/**
 * Process a single tile using Gemini-3-Pro via Cloud Function
 * @param {number} z - Zoom level
 * @param {number} x - Tile X coordinate
 * @param {number} y - Tile Y coordinate
 * @param {string} accessToken - Mapbox access token
 * @param {string} prompt - Prompt for Gemini
 * @param {string} styleKey - Style key for caching
 * @param {Array} neighborTiles - Optional array of {url, position} for style reference
 */
const processGeminiTileViaBackend = async (z, x, y, accessToken, prompt, styleKey, neighborTiles = [], rawMode = false, referencePhoto = null) => {
  const tileUrl = `https://api.mapbox.com/v4/mapbox.satellite/${z}/${x}/${y}@2x.jpg90?access_token=${accessToken}`;

  const processGeminiTile = httpsCallable(functions, 'processGeminiTile', { timeout: 300000 }); // 5 minutes timeout

  try {
    const result = await processGeminiTile({
      tileUrl,
      prompt,
      styleKey,
      z,
      x,
      y,
      neighborTiles, // Array of { url: string, position: "top" | "bottom" | "left" | "right" }
      rawMode, // Skip text overlays, neighbor tiles, and pre-prompt text
      referencePhoto // Base64 data URL of uploaded reference photo (sent as first image)
    });

    return {
      z,
      x,
      y,
      blobUrl: result.data.url,
      fullPrompt: result.data.fullPrompt, // Return the actual prompt from backend
      debugImages: result.data.debugImages, // Return the actual images sent to Gemini
      geminiText: result.data.text, // Return Gemini's text response
      thoughtsSummary: result.data.thoughtsSummary, // Return Gemini's thought summary
      fromCache: false
    };
  } catch (error) {
    console.error(`Gemini processing failed for tile ${z}/${x}/${y}:`, error);
    throw error;
  }
};

/**
 * Get neighboring tiles that have already been processed
 * Returns array of { url, position } for tiles that exist in processedTiles
 */
const getNeighborTiles = (z, x, y, processedTiles) => {
  const neighbors = [];

  // Check all 4 directions
  const directions = [
    { dx: 0, dy: -1, position: 'top' },
    { dx: 0, dy: 1, position: 'bottom' },
    { dx: -1, dy: 0, position: 'left' },
    { dx: 1, dy: 0, position: 'right' },
  ];

  for (const dir of directions) {
    const neighborKey = `${z}/${x + dir.dx}/${y + dir.dy}`;
    const neighborUrl = processedTiles[neighborKey];
    if (neighborUrl) {
      neighbors.push({
        url: neighborUrl,
        position: dir.position
      });
    }
  }

  return neighbors;
};

const GpxViewerPage = ({ user }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAdminView();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const fileInputRef = useRef(null);
  const puckMarkerRef = useRef(null);
  const animationRef = useRef(null);
  const cumulativeDistancesRef = useRef(null);

  // Drag/scrub refs
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartDistanceRef = useRef(0);
  const currentBearingRef = useRef(0); // Track current bearing for smooth transitions

  const [gpxData, setGpxData] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('sample-route.gpx');

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0); // 0 to 1
  const [currentDistance, setCurrentDistance] = useState(0); // meters traveled
  const [isScrubMode, setIsScrubMode] = useState(false); // True when user can drag to scrub

  // Map style state - cycles through: satellite-base -> satellite -> outdoor
  const [mapStyleIndex, setMapStyleIndex] = useState(2); // 0: satellite-base, 1: satellite, 2: outdoor (default: 3D Terrain)
  const [zoomLevel, setZoomLevel] = useState(13);
  const [isOverheadView, setIsOverheadView] = useState(true); // Track overhead vs 3D view

  // Camera settings for FreeCamera mode (adjustable via UI) - persisted to localStorage
  const [cameraAltitude, setCameraAltitude] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_cameraAltitude');
    return saved ? Number(saved) : DEFAULT_CAMERA_ALTITUDE;
  });
  const [cameraDistance, setCameraDistance] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_cameraDistance');
    return saved ? Number(saved) : DEFAULT_CAMERA_DISTANCE_BEHIND;
  });
  // Refs to access latest values in animation loop (avoids stale closure)
  const cameraAltitudeRef = useRef(cameraAltitude);
  const cameraDistanceRef = useRef(cameraDistance);

  // Animation speed setting (km/h) - persisted to localStorage
  const [animationSpeedKmh, setAnimationSpeedKmh] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_animationSpeed');
    return saved ? Number(saved) : DEFAULT_ANIMATION_SPEED_KMH;
  });
  const animationSpeedRef = useRef(animationSpeedKmh);

  // Map dimensions (adjustable via UI) - persisted to localStorage
  const [mapWidth, setMapWidth] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_mapWidth');
    return saved ? Number(saved) : 100; // percentage
  });
  const [mapHeight, setMapHeight] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_mapHeight');
    return saved ? Number(saved) : 500; // pixels
  });

  // Tile processing state
  const [processedTiles, setProcessedTiles] = useState({}); // { "z/x/y": blobUrl }
  const [isProcessingTiles, setIsProcessingTiles] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [stopRequested, setStopRequested] = useState(false); // Flag to stop processing after current tile
  const stopRequestedRef = useRef(false); // Ref for immediate access in async loop
  const [routeTileCount, setRouteTileCount] = useState(0);
  const [saveTilesEnabled, setSaveTilesEnabled] = useState(false); // Toggle to save input/output tiles
  const [processingStyle, setProcessingStyle] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_processingStyle');
    return saved || 'edge-detection';
  }); // Processing style key - persisted

  // Gemini processing state - persist to localStorage
  const [geminiPrompt, setGeminiPrompt] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_geminiPrompt');
    return saved || 'Transform this satellite map tile into a stylized artistic illustration while preserving the geography and features.';
  });
  const [customStyleName, setCustomStyleName] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_customStyleName');
    return saved || '';
  }); // User-provided style name for Gemini
  const [customStyles, setCustomStyles] = useState([]); // List of user-created Gemini styles
  const [processAllTiles, setProcessAllTiles] = useState(false); // Toggle between single tile (debug) and all tiles
  const [rawMode, setRawMode] = useState(() => {
    const saved = localStorage.getItem('gpxViewer_rawMode');
    return saved === 'true';
  }); // Skip text overlays, neighbor tiles, and pre-prompt text
  const [referencePhoto, setReferencePhoto] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('gpxViewer_referencePhoto');
    return saved || null;
  }); // Base64 data URL of uploaded reference photo
  const referencePhotoInputRef = useRef(null);

  // Debug state for tile generation visualization
  const [debugInfo, setDebugInfo] = useState(null); // { inputUrl, neighborTiles, constructedPrompt, outputUrl, tileCoords }

  // Tile deletion state
  const [deletingTile, setDeletingTile] = useState(null); // Currently deleting tile key

  // Lightbox state for viewing larger tile images
  const [lightboxTile, setLightboxTile] = useState(null); // { key, url } of the tile to display in lightbox

  // Map styles
  const OUTDOOR_STYLE = 'mapbox://styles/samuelcollin3333/cmf525thz00xg01rk0ewt0i6f';
  const SATELLITE_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12';
  const MAP_STYLES = [
    { name: 'Satellite Base', key: 'satellite-base', style: null }, // Uses SATELLITE_BASE_STYLE object
    { name: 'Satellite', key: 'satellite', style: SATELLITE_STYLE },
    { name: '3D Terrain', key: 'outdoor', style: OUTDOOR_STYLE }
  ];

  // Custom tile zoom range - show custom tiles only within this range
  const CUSTOM_TILE_MIN_ZOOM = 12.5;
  const CUSTOM_TILE_MAX_ZOOM = 13.5;

  // Ref to store processed tiles for access in tile loading
  const processedTilesRef = useRef({});

  // Check if user is admin - redirect to home if not
  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/');
      return;
    }
  }, [user, isAdmin, navigate]);

  // Update ref when processedTiles state changes
  useEffect(() => {
    processedTilesRef.current = processedTiles;
  }, [processedTiles]);

  // Persist geminiPrompt to localStorage
  useEffect(() => {
    localStorage.setItem('gpxViewer_geminiPrompt', geminiPrompt);
  }, [geminiPrompt]);

  // Persist customStyleName to localStorage
  useEffect(() => {
    localStorage.setItem('gpxViewer_customStyleName', customStyleName);
  }, [customStyleName]);

  // Persist processingStyle to localStorage
  useEffect(() => {
    localStorage.setItem('gpxViewer_processingStyle', processingStyle);
  }, [processingStyle]);

  // Persist referencePhoto to localStorage
  useEffect(() => {
    if (referencePhoto) {
      localStorage.setItem('gpxViewer_referencePhoto', referencePhoto);
    } else {
      localStorage.removeItem('gpxViewer_referencePhoto');
    }
  }, [referencePhoto]);

  // Handle Escape key to close lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxTile) {
        setLightboxTile(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxTile]);

  // Resize map when dimensions change
  useEffect(() => {
    if (map.current) {
      // Small delay to let the DOM update first
      setTimeout(() => {
        map.current.resize();
      }, 100);
    }
  }, [mapWidth, mapHeight]);

  // Re-apply camera position when altitude/distance sliders change during scrub/pause mode
  useEffect(() => {
    if (!map.current || !gpxData || !cumulativeDistancesRef.current) return;
    if (!isScrubMode && !isPaused) return; // Only apply when scrubbing or paused
    if (isOverheadView) return; // Only for 3D mode

    // Get current position based on current distance
    const position = getPositionAtDistance(
      gpxData.coordinates,
      cumulativeDistancesRef.current,
      currentDistance
    );

    // Use current bearing from ref
    const bearing = currentBearingRef.current;
    const behindBearing = (bearing + 180) % 360;
    const cameraPosition = offsetPosition(position, behindBearing, cameraDistance);

    const camera = map.current.getFreeCameraOptions();
    camera.position = mapboxgl.MercatorCoordinate.fromLngLat(cameraPosition, cameraAltitude);
    camera.lookAtPoint(position);
    map.current.setFreeCameraOptions(camera);
  }, [cameraAltitude, cameraDistance, gpxData, isScrubMode, isPaused, isOverheadView, currentDistance]);

  // Load existing custom styles from Firebase Storage on page load
  useEffect(() => {
    const loadCustomStylesFromStorage = async () => {
      try {
        const tilesRef = ref(storage, 'tiles/processed/');
        const result = await listAll(tilesRef);

        // Extract style names from folder prefixes
        const styleNames = result.prefixes.map(prefix => {
          // prefix.name gives us the folder name (style name)
          return prefix.name;
        });

        if (styleNames.length > 0) {
          console.log('[GPX_VIEWER] Found existing custom styles:', styleNames);
          setCustomStyles(styleNames);
        }
      } catch (error) {
        console.error('[GPX_VIEWER] Error loading custom styles from storage:', error);
        // Silently fail - custom styles just won't be loaded
      }
    };

    loadCustomStylesFromStorage();
  }, []);

  // Update map to show processed tiles
  const updateMapWithProcessedTiles = useCallback((tiles) => {
    if (!map.current) return;

    // Check if style is loaded - if not, wait for it
    if (!map.current.isStyleLoaded()) {
      map.current.once('style.load', () => {
        updateMapWithProcessedTiles(tiles);
      });
      return;
    }

    const hasCustomTiles = Object.keys(tiles).length > 0;

    // Remove existing processed tile layers and sources
    const existingLayers = map.current.getStyle().layers || [];
    existingLayers.forEach(layer => {
      if (layer.id.startsWith('processed-tile-layer-') || layer.id === 'grey-background-layer') {
        map.current.removeLayer(layer.id);
      }
    });

    // Remove existing sources
    const existingSources = Object.keys(map.current.getStyle().sources || {});
    existingSources.forEach(sourceId => {
      if (sourceId.startsWith('processed-tile-') || sourceId === 'grey-background-source') {
        map.current.removeSource(sourceId);
      }
    });

    // Update satellite layer opacity based on whether we have custom tiles
    // When custom tiles exist, fade out satellite in the custom tile zoom range
    if (map.current.getLayer('satellite-layer')) {
      if (hasCustomTiles) {
        map.current.setPaintProperty('satellite-layer', 'raster-opacity', [
          'step',
          ['zoom'],
          1,                      // opacity 1 below 12.5
          CUSTOM_TILE_MIN_ZOOM, 0, // opacity 0 at 12.5+ (grey/custom tiles visible)
          CUSTOM_TILE_MAX_ZOOM, 1  // opacity 1 at 13.5+ (back to satellite)
        ]);
      } else {
        // No custom tiles - full opacity everywhere
        map.current.setPaintProperty('satellite-layer', 'raster-opacity', 1);
      }
    }

    // Add grey background layer if we have custom tiles
    if (hasCustomTiles) {
      // Add a geojson source covering the whole world for the grey background
      map.current.addSource('grey-background-source', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [-180, -85],
              [180, -85],
              [180, 85],
              [-180, 85],
              [-180, -85]
            ]]
          }
        }
      });

      // Add grey background layer - between satellite and processed tiles
      const beforeLayerId = map.current.getLayer('route-outline') ? 'route-outline' : undefined;
      map.current.addLayer({
        id: 'grey-background-layer',
        type: 'fill',
        source: 'grey-background-source',
        paint: {
          'fill-color': '#808080',
          'fill-opacity': [
            'step',
            ['zoom'],
            0,                      // opacity 0 below 12.5
            CUSTOM_TILE_MIN_ZOOM, 1, // opacity 1 at 12.5+
            CUSTOM_TILE_MAX_ZOOM, 0  // opacity 0 at 13.5+
          ]
        }
      }, beforeLayerId);
    }

    // Create individual sources and layers for each processed tile
    Object.entries(tiles).forEach(([key, blobUrl]) => {
      const [z, x, y] = key.split('/').map(Number);

      // Calculate tile bounds
      const n = Math.pow(2, z);
      const west = (x / n) * 360 - 180;
      const east = ((x + 1) / n) * 360 - 180;
      const northRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * y / n)));
      const southRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * (y + 1) / n)));
      const north = northRad * 180 / Math.PI;
      const south = southRad * 180 / Math.PI;

      const sourceId = `processed-tile-${key.replace(/\//g, '-')}`;
      const layerId = `processed-tile-layer-${key.replace(/\//g, '-')}`;

      // Add as image source with coordinates
      map.current.addSource(sourceId, {
        type: 'image',
        url: blobUrl,
        coordinates: [
          [west, north], // top-left
          [east, north], // top-right
          [east, south], // bottom-right
          [west, south]  // bottom-left
        ]
      });

      // Add layer for this tile - BEFORE route layers so route renders on top
      const beforeLayerId = map.current.getLayer('route-outline') ? 'route-outline' : undefined;
      map.current.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': [
            'step',
            ['zoom'],
            0,                      // opacity 0 below 12.5
            CUSTOM_TILE_MIN_ZOOM, 1, // opacity 1 at 12.5+
            CUSTOM_TILE_MAX_ZOOM, 0  // opacity 0 at 13.5+
          ]
        }
      }, beforeLayerId);
    });

    console.log(`Added ${Object.keys(tiles).length} processed tile layers to map (grey background: ${hasCustomTiles})`);
  }, [CUSTOM_TILE_MIN_ZOOM, CUSTOM_TILE_MAX_ZOOM]);

  // Delete a tile from storage and remove from map
  const deleteTile = useCallback(async (tileKey) => {
    if (!tileKey || deletingTile) return;

    const [z, x, y] = tileKey.split('/');
    const styleKey = customStyleName || processingStyle;
    const tilePath = `tiles/processed/${styleKey}/${z}_${x}_${y}.png`;

    setDeletingTile(tileKey);

    try {
      // Delete from Firebase Storage
      const tileRef = ref(storage, tilePath);
      await deleteObject(tileRef);
      console.log(`[TILE_DELETE] Deleted tile from storage: ${tilePath}`);

      // Remove from local state
      const newProcessedTiles = { ...processedTiles };
      delete newProcessedTiles[tileKey];
      setProcessedTiles(newProcessedTiles);
      processedTilesRef.current = newProcessedTiles;

      // Update map
      if (map.current) {
        const sourceId = `processed-tile-${tileKey.replace(/\//g, '-')}`;
        const layerId = `processed-tile-layer-${tileKey.replace(/\//g, '-')}`;

        if (map.current.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
        if (map.current.getSource(sourceId)) {
          map.current.removeSource(sourceId);
        }
      }
    } catch (error) {
      console.error(`[TILE_DELETE] Failed to delete tile ${tilePath}:`, error);
      alert(`Failed to delete tile: ${error.message}`);
    } finally {
      setDeletingTile(null);
    }
  }, [customStyleName, processingStyle, processedTiles, deletingTile]);


  // Process all route tiles (local processing - edge detection, grayscale, etc.)
  const processRouteTiles = useCallback(async () => {
    if (!gpxData || !gpxData.coordinates.length) return;

    // Check if this is a Gemini style
    if (processingStyle === 'gemini-3-pro' || customStyles.includes(processingStyle)) {
      console.log('Gemini styles should use processGeminiTiles instead');
      return;
    }

    setIsProcessingTiles(true);
    setProcessingProgress(0);

    const tiles = getRouteTiles(gpxData.coordinates, TILE_PROCESSING_ZOOM);
    setRouteTileCount(tiles.length);

    console.log(`Processing ${tiles.length} tiles at zoom ${TILE_PROCESSING_ZOOM} (style: ${processingStyle}, save: ${saveTilesEnabled})`);

    const newProcessedTiles = {};
    let completed = 0;
    let cachedCount = 0;
    let processedCount = 0;

    // Process tiles in parallel batches of 4 (or 1 if saving to avoid upload spam)
    const batchSize = saveTilesEnabled ? 1 : 4;
    for (let i = 0; i < tiles.length; i += batchSize) {
      const batch = tiles.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(tile => fetchAndProcessTile(tile.z, tile.x, tile.y, MAPBOX_ACCESS_TOKEN, processingStyle, saveTilesEnabled))
      );

      results.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          const { z, x, y, blobUrl, fromCache } = result.value;
          newProcessedTiles[`${z}/${x}/${y}`] = blobUrl;
          if (fromCache) {
            cachedCount++;
          } else {
            processedCount++;
          }
        } else {
          console.error(`Failed to process tile:`, result.reason);
        }
        completed++;
        setProcessingProgress(Math.round((completed / tiles.length) * 100));
      });

      // Small delay between tiles when saving to avoid overwhelming uploads
      if (saveTilesEnabled && i + batchSize < tiles.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setProcessedTiles(newProcessedTiles);
    processedTilesRef.current = newProcessedTiles;
    setIsProcessingTiles(false);

    console.log(`Loaded ${Object.keys(newProcessedTiles).length} tiles: ${cachedCount} from cache, ${processedCount} processed locally`);

    // Update the map with processed tiles
    if (map.current && Object.keys(newProcessedTiles).length > 0) {
      updateMapWithProcessedTiles(newProcessedTiles);
    }
  }, [gpxData, processingStyle, saveTilesEnabled, customStyles]);

  // Process tiles using Gemini-3-Pro
  const processGeminiTiles = useCallback(async () => {
    if (!gpxData || !gpxData.coordinates.length) return;

    // Determine the style key to use
    const styleKey = processingStyle === 'gemini-3-pro'
      ? (customStyleName.trim() || 'gemini-default')
      : processingStyle;

    // Add custom style to the list if it's new
    if (customStyleName.trim() && !customStyles.includes(customStyleName.trim())) {
      setCustomStyles(prev => [...prev, customStyleName.trim()]);
    }

    setIsProcessingTiles(true);
    setProcessingProgress(0);

    const tiles = getRouteTiles(gpxData.coordinates, TILE_PROCESSING_ZOOM);
    setRouteTileCount(tiles.length);

    const mode = processAllTiles ? 'ALL' : 'SINGLE';
    console.log(`[GEMINI] Processing ${tiles.length} tiles for style: ${styleKey} (mode: ${mode})`);

    // Collect all cached tiles and uncached tiles
    const newProcessedTiles = {};
    const tilesToProcess = [];

    for (let i = 0; i < tiles.length; i++) {
      const tile = tiles[i];
      setProcessingProgress(Math.round((i / tiles.length) * 30)); // First 30% is checking cache

      const existingUrl = await getExistingTileUrl(tile.z, tile.x, tile.y, styleKey);
      if (existingUrl) {
        console.log(`[GEMINI] Cached: ${tile.z}/${tile.x}/${tile.y}`);
        newProcessedTiles[`${tile.z}/${tile.x}/${tile.y}`] = existingUrl;
      } else {
        tilesToProcess.push(tile);
      }
    }

    // If we have cached tiles, display them first
    if (Object.keys(newProcessedTiles).length > 0) {
      setProcessedTiles({ ...newProcessedTiles });
      processedTilesRef.current = { ...newProcessedTiles };
      if (map.current) {
        updateMapWithProcessedTiles({ ...newProcessedTiles });
      }
      console.log(`[GEMINI] Loaded ${Object.keys(newProcessedTiles).length} cached tiles`);
    }

    // Determine which tiles to process based on mode
    const tileBatch = processAllTiles ? tilesToProcess : tilesToProcess.slice(0, 1);

    // Reset stop flag at start of processing
    setStopRequested(false);
    stopRequestedRef.current = false;

    if (tileBatch.length === 0) {
      console.log(`[GEMINI] All ${tiles.length} tiles already cached!`);
    } else {
      console.log(`[GEMINI] Processing ${tileBatch.length} uncached tile(s)...`);

      // Process tiles sequentially to avoid overwhelming the API
      for (let i = 0; i < tileBatch.length; i++) {
        // Check if stop was requested before starting next tile
        if (stopRequestedRef.current) {
          console.log(`[GEMINI] Stop requested after tile ${i}/${tileBatch.length}. Stopping processing.`);
          break;
        }

        const tile = tileBatch[i];
        const progressBase = 30; // After cache check
        const progressRange = 65; // 30% to 95%
        const progress = progressBase + Math.round((i / tileBatch.length) * progressRange);
        setProcessingProgress(progress);

        // Get neighboring tiles that have already been processed for style consistency (skip in raw mode)
        const neighborTiles = rawMode ? [] : getNeighborTiles(tile.z, tile.x, tile.y, newProcessedTiles);
        console.log(`[GEMINI] Processing tile ${i + 1}/${tileBatch.length}: ${tile.z}/${tile.x}/${tile.y} with ${neighborTiles.length} neighbor reference(s)${rawMode ? ' (RAW MODE)' : ''}`);

        try {
          const result = await processGeminiTileViaBackend(
            tile.z, tile.x, tile.y,
            MAPBOX_ACCESS_TOKEN,
            geminiPrompt,
            styleKey,
            neighborTiles,
            rawMode,
            referencePhoto
          );
          newProcessedTiles[`${tile.z}/${tile.x}/${tile.y}`] = result.blobUrl;
          console.log(`[GEMINI] Tile ${i + 1}/${tileBatch.length} processed successfully (neighbors: ${neighborTiles.map(n => n.position).join(', ') || 'none'})`);

          // Update debug info with this generation's details
          // Use actual data from backend (exactly what was sent to Gemini)
          const newDebugInfo = {
            tileCoords: `${tile.z}/${tile.x}/${tile.y}`,
            referencePhoto: result.debugImages?.referencePhoto || null, // Uploaded reference photo (sent first)
            inputUrl: result.debugImages?.inputImage, // Actual image sent to Gemini
            neighborTiles: result.debugImages?.neighborImages || [], // Actual neighbor images sent
            constructedPrompt: result.fullPrompt, // Actual prompt sent to Gemini
            outputUrl: result.blobUrl,
            geminiText: result.geminiText, // Gemini's text response
            thoughtsSummary: result.thoughtsSummary, // Gemini's thought summary
            timestamp: Date.now() // Force unique object each time
          };
          console.log('[GEMINI] Setting debug info:', newDebugInfo.tileCoords, newDebugInfo.outputUrl);
          setDebugInfo(newDebugInfo);

          // Update map progressively so user sees results
          // Create explicit new object reference
          const updatedTiles = { ...newProcessedTiles };
          console.log('[GEMINI] Setting processed tiles, count:', Object.keys(updatedTiles).length);
          setProcessedTiles(updatedTiles);
          processedTilesRef.current = updatedTiles;

          // Update map directly
          if (map.current) {
            updateMapWithProcessedTiles(updatedTiles);
          }
        } catch (error) {
          console.error(`[GEMINI] Failed to process tile ${tile.z}/${tile.x}/${tile.y}:`, error);
        }
      }
    }

    setProcessingProgress(100);
    setProcessedTiles(newProcessedTiles);
    processedTilesRef.current = newProcessedTiles;
    setIsProcessingTiles(false);
    setStopRequested(false);
    stopRequestedRef.current = false;

    // Final map update
    if (map.current && Object.keys(newProcessedTiles).length > 0) {
      updateMapWithProcessedTiles(newProcessedTiles);
      console.log(`[GEMINI] Complete! Map updated with ${Object.keys(newProcessedTiles).length} tiles`);
    }
  }, [gpxData, processingStyle, customStyleName, customStyles, geminiPrompt, processAllTiles, rawMode, referencePhoto, updateMapWithProcessedTiles]);

  // Handler for processing tiles - always uses Gemini
  const handleProcessTiles = useCallback(() => {
    processGeminiTiles();
  }, [processGeminiTiles]);

  // Handler for stopping tile processing after current tile completes
  const handleStopProcessing = useCallback(() => {
    console.log('[GEMINI] Stop requested by user');
    setStopRequested(true);
    stopRequestedRef.current = true;
  }, []);

  // Check for cached tiles when processing style changes
  const loadCachedTilesForStyle = useCallback(async (styleKey) => {
    if (!gpxData || !gpxData.coordinates.length) return;

    setIsProcessingTiles(true);
    setProcessingProgress(0);

    const tiles = getRouteTiles(gpxData.coordinates, TILE_PROCESSING_ZOOM);
    setRouteTileCount(tiles.length);

    console.log(`Checking for cached tiles at zoom ${TILE_PROCESSING_ZOOM} (style: ${styleKey})`);

    const cachedTiles = {};
    let completed = 0;
    let foundCount = 0;

    // Check tiles in parallel batches
    const batchSize = 8;
    for (let i = 0; i < tiles.length; i += batchSize) {
      const batch = tiles.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(async (tile) => {
          const url = await getExistingTileUrl(tile.z, tile.x, tile.y, styleKey);
          return { z: tile.z, x: tile.x, y: tile.y, url };
        })
      );

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.url) {
          const { z, x, y, url } = result.value;
          cachedTiles[`${z}/${x}/${y}`] = url;
          foundCount++;
        }
        completed++;
        setProcessingProgress(Math.round((completed / tiles.length) * 100));
      });
    }

    setIsProcessingTiles(false);

    if (foundCount > 0) {
      console.log(`Found ${foundCount} cached tiles for style: ${styleKey}`);
      setProcessedTiles(cachedTiles);
      processedTilesRef.current = cachedTiles;

      // Update the map with cached tiles
      if (map.current) {
        updateMapWithProcessedTiles(cachedTiles);
      }
    } else {
      console.log(`No cached tiles found for style: ${styleKey}`);
      // Clear processed tiles if none found for new style
      setProcessedTiles({});
      processedTilesRef.current = {};
      // Remove tile layers from map
      if (map.current) {
        updateMapWithProcessedTiles({});
      }
    }
  }, [gpxData, updateMapWithProcessedTiles]);

  // Effect to load cached tiles when processing style changes
  useEffect(() => {
    if (gpxData && gpxData.coordinates.length > 0) {
      loadCachedTilesForStyle(processingStyle);
    }
  }, [processingStyle, gpxData, loadCachedTilesForStyle]);

  // Base satellite style (no custom tiles overlay)
  const SATELLITE_BASE_STYLE = {
    version: 8,
    name: 'Satellite Base Style',
    sources: {
      'mapbox-satellite': {
        type: 'raster',
        tiles: [
          `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${MAPBOX_ACCESS_TOKEN}`
        ],
        tileSize: 512,
        minzoom: 0,
        maxzoom: 22
      }
    },
    layers: [
      {
        id: 'satellite-layer',
        type: 'raster',
        source: 'mapbox-satellite',
        paint: {
          'raster-opacity': 1
        }
      }
    ]
  };

  // Function to add route layers to the map
  const addRouteToMap = useCallback(() => {
    if (!map.current || !gpxData) return;

    // Check if style is loaded - if not, wait for it
    if (!map.current.isStyleLoaded()) {
      map.current.once('style.load', () => {
        addRouteToMap();
      });
      return;
    }

    // Remove existing route if present
    if (map.current.getSource('route')) {
      if (map.current.getLayer('route-line')) {
        map.current.removeLayer('route-line');
      }
      if (map.current.getLayer('route-outline')) {
        map.current.removeLayer('route-outline');
      }
      map.current.removeSource('route');
    }

    // Add route source
    map.current.addSource('route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: gpxData.coordinates
        }
      }
    });

    // Add outline layer (wider, darker line behind)
    map.current.addLayer({
      id: 'route-outline',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#000000',
        'line-width': 6,
        'line-opacity': 0.3
      }
    });

    // Add main route line
    map.current.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#FC5200',
        'line-width': 4
      }
    });
  }, [gpxData]);

  // Cycle through map styles: satellite-base -> satellite -> outdoor -> satellite-base
  const cycleMapStyle = useCallback(() => {
    if (!map.current) return;

    const nextIndex = (mapStyleIndex + 1) % MAP_STYLES.length;
    const nextStyle = MAP_STYLES[nextIndex];
    setMapStyleIndex(nextIndex);

    // Use custom style object for satellite-base, mapbox URL for others
    const styleToSet = nextStyle.style || SATELLITE_BASE_STYLE;
    map.current.setStyle(styleToSet);

    // Re-add route and markers after style loads
    map.current.once('style.load', () => {
      // Add terrain for satellite style (the 3D outdoor style has terrain built-in)
      if (nextStyle.key === 'satellite') {
        if (!map.current.getSource('mapbox-dem')) {
          map.current.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14
          });
        }
        map.current.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
      }

      addRouteToMap();

      // Re-add processed tiles if we have them
      if (Object.keys(processedTilesRef.current).length > 0) {
        updateMapWithProcessedTiles(processedTilesRef.current);
      }

      // Re-add markers
      if (gpxData) {
        // Remove existing markers and re-add them
        document.querySelectorAll('.gpx-marker').forEach(el => el.remove());

        const startCoord = gpxData.coordinates[0];
        const endCoord = gpxData.coordinates[gpxData.coordinates.length - 1];

        // Start marker (green)
        const startEl = document.createElement('div');
        startEl.className = 'gpx-marker gpx-marker-start';
        startEl.innerHTML = '<div class="marker-inner">S</div>';
        new mapboxgl.Marker(startEl).setLngLat(startCoord).addTo(map.current);

        // End marker (red)
        const endEl = document.createElement('div');
        endEl.className = 'gpx-marker gpx-marker-end';
        endEl.innerHTML = '<div class="marker-inner">F</div>';
        new mapboxgl.Marker(endEl).setLngLat(endCoord).addTo(map.current);

        // Re-add puck if it was visible
        if (puckMarkerRef.current && (isAnimating || isPaused || animationProgress > 0)) {
          const puckPosition = puckMarkerRef.current.getLngLat();
          puckMarkerRef.current.remove();

          const puckEl = document.createElement('div');
          puckEl.className = 'gpx-puck';
          puckEl.innerHTML = '<div class="puck-inner"></div><div class="puck-trail"></div>';
          puckMarkerRef.current = new mapboxgl.Marker(puckEl)
            .setLngLat(puckPosition)
            .addTo(map.current);
        }
      }
    });
  }, [mapStyleIndex, MAP_STYLES, SATELLITE_BASE_STYLE, gpxData, addRouteToMap, updateMapWithProcessedTiles, isAnimating, isPaused, animationProgress]);

  // Toggle between overhead view and 3D view
  const toggleOverheadView = useCallback(() => {
    if (!map.current) return;

    const newIsOverhead = !isOverheadView;
    setIsOverheadView(newIsOverhead);

    if (newIsOverhead) {
      // Switch to overhead view (looking straight down)
      map.current.easeTo({
        pitch: 0,
        bearing: 0,
        duration: 500
      });
    } else {
      // Switch to 3D view (angled)
      map.current.easeTo({
        pitch: CAMERA_PITCH,
        duration: 500
      });
    }
  }, [isOverheadView]);

  // Load default GPX on mount
  useEffect(() => {
    const loadDefaultGPX = async () => {
      try {
        const response = await fetch(DEFAULT_GPX_URL);
        if (!response.ok) {
          throw new Error('Failed to load default GPX file');
        }
        const gpxString = await response.text();
        const parsed = parseGPX(gpxString);
        setGpxData(parsed);
        setStats(calculateStats(parsed.coordinates, parsed.elevations));
        setFileName('Morning_Ride (4).gpx');
        setError(null);
      } catch (err) {
        console.error('Error loading default GPX:', err);
        setError('Failed to load default GPX file. Please upload your own.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDefaultGPX();
  }, []);

  // Initialize and update map
  useEffect(() => {
    if (!gpxData || !gpxData.coordinates.length) return;

    // Initialize map if not already done
    if (!map.current && mapContainer.current) {
      // Get initial style based on mapStyleIndex (default: 2 = 3D Terrain)
      const initialStyle = MAP_STYLES[mapStyleIndex].style || SATELLITE_BASE_STYLE;
      console.log('[GPX_VIEWER] Initializing map with style index:', mapStyleIndex, 'style:', MAP_STYLES[mapStyleIndex].name, 'initialStyle:', typeof initialStyle === 'string' ? initialStyle : 'SATELLITE_BASE_STYLE object');

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: initialStyle, // Use style based on mapStyleIndex (default: 3D Terrain)
        center: gpxData.coordinates[0],
        zoom: 13,
        attributionControl: false,
        pitch: 0, // Start flat, will pitch when animation starts
        bearing: 0
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      map.current.addControl(new mapboxgl.ScaleControl(), 'bottom-left');

      // Track zoom level changes
      map.current.on('zoom', () => {
        setZoomLevel(map.current.getZoom());
      });

      map.current.on('load', () => {
        // Custom tile style - no terrain needed
        addRouteToMap();
        addMarkersToMap();
        fitMapToBounds();
      });

    } else if (map.current) {
      // Update existing map with new route
      addRouteToMap();
      addMarkersToMap();
      fitMapToBounds();
    }

    function addMarkersToMap() {
      // Remove existing markers
      document.querySelectorAll('.gpx-marker').forEach(el => el.remove());

      const startCoord = gpxData.coordinates[0];
      const endCoord = gpxData.coordinates[gpxData.coordinates.length - 1];

      // Start marker (green)
      const startEl = document.createElement('div');
      startEl.className = 'gpx-marker gpx-marker-start';
      startEl.innerHTML = '<div class="marker-inner">S</div>';
      new mapboxgl.Marker(startEl).setLngLat(startCoord).addTo(map.current);

      // End marker (red)
      const endEl = document.createElement('div');
      endEl.className = 'gpx-marker gpx-marker-end';
      endEl.innerHTML = '<div class="marker-inner">F</div>';
      new mapboxgl.Marker(endEl).setLngLat(endCoord).addTo(map.current);
    }

    function fitMapToBounds() {
      const bounds = new mapboxgl.LngLatBounds();
      gpxData.coordinates.forEach(coord => bounds.extend(coord));
      map.current.fitBounds(bounds, { padding: 50, duration: 1000 });
    }

    return () => {
      // Cleanup markers on unmount
      document.querySelectorAll('.gpx-marker').forEach(el => el.remove());
    };
  }, [gpxData, addRouteToMap]);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.gpx')) {
      setError('Please select a valid GPX file');
      return;
    }

    setIsLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const gpxString = e.target.result;
        const parsed = parseGPX(gpxString);

        if (parsed.coordinates.length === 0) {
          throw new Error('No coordinates found in GPX file');
        }

        setGpxData(parsed);
        setStats(calculateStats(parsed.coordinates, parsed.elevations));
        setFileName(file.name);
        setError(null);
      } catch (err) {
        console.error('Error parsing GPX:', err);
        setError(err.message || 'Failed to parse GPX file');
        setGpxData(null);
        setStats(null);
      } finally {
        setIsLoading(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
      setIsLoading(false);
    };

    reader.readAsText(file);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Smoothly interpolate between bearings, handling the 0/360 wraparound
  // Uses exponential smoothing with a history buffer for extra smoothness
  const bearingHistoryRef = useRef([]);
  const BEARING_HISTORY_SIZE = 8; // Number of frames to average over

  const smoothBearing = useCallback((targetBearing, smoothingFactor = 0.08) => {
    let currentBearing = currentBearingRef.current;

    // Calculate the shortest angular distance
    let delta = targetBearing - currentBearing;

    // Handle wraparound (e.g., going from 350° to 10°)
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;

    // Apply primary smoothing
    const newBearing = currentBearing + delta * smoothingFactor;

    // Normalize to 0-360
    const normalizedBearing = ((newBearing % 360) + 360) % 360;

    // Add to history buffer for additional smoothing
    bearingHistoryRef.current.push(normalizedBearing);
    if (bearingHistoryRef.current.length > BEARING_HISTORY_SIZE) {
      bearingHistoryRef.current.shift();
    }

    // Calculate weighted average of history (more recent = higher weight)
    let weightedSum = 0;
    let weightTotal = 0;
    const history = bearingHistoryRef.current;

    for (let i = 0; i < history.length; i++) {
      const weight = i + 1; // Linear weights: 1, 2, 3, ...
      // Handle wraparound in averaging
      let bearing = history[i];
      if (history.length > 1) {
        const refBearing = history[history.length - 1];
        let diff = bearing - refBearing;
        if (diff > 180) bearing -= 360;
        if (diff < -180) bearing += 360;
      }
      weightedSum += bearing * weight;
      weightTotal += weight;
    }

    const smoothedBearing = ((weightedSum / weightTotal % 360) + 360) % 360;
    currentBearingRef.current = smoothedBearing;

    return currentBearingRef.current;
  }, []);

  // Update puck and map position for a given distance (used by both animation and scrubbing)
  const updatePositionAtDistance = useCallback((distance, smooth = true) => {
    if (!gpxData || !map.current || !cumulativeDistancesRef.current) return;

    const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1];
    const clampedDistance = Math.max(0, Math.min(distance, totalDistance));

    const position = getPositionAtDistance(
      gpxData.coordinates,
      cumulativeDistancesRef.current,
      clampedDistance
    );

    const targetBearing = getBearingAtDistance(
      gpxData.coordinates,
      cumulativeDistancesRef.current,
      clampedDistance
    );

    // Apply smoothing for scrubbing, instant for animation
    const bearing = smooth ? smoothBearing(targetBearing) : targetBearing;
    if (!smooth) {
      currentBearingRef.current = targetBearing;
    }

    if (puckMarkerRef.current) {
      puckMarkerRef.current.setLngLat(position);
    }

    // Use FreeCamera API to set fixed altitude, preventing zoom flickering over terrain
    // Position camera BEHIND the puck and look at the puck
    // Use refs to get latest values (avoids stale closure from slider changes)
    const behindBearing = (bearing + 180) % 360; // Opposite of travel direction
    const cameraPosition = offsetPosition(position, behindBearing, cameraDistanceRef.current);

    const camera = map.current.getFreeCameraOptions();
    camera.position = mapboxgl.MercatorCoordinate.fromLngLat(cameraPosition, cameraAltitudeRef.current);
    camera.lookAtPoint(position); // Look at the puck (puck will be in center of view)
    map.current.setFreeCameraOptions(camera);

    setCurrentDistance(clampedDistance);
    setAnimationProgress(clampedDistance / totalDistance);
  }, [gpxData, smoothBearing]);

  // Animation controls
  const startAnimation = useCallback(() => {
    if (!gpxData || !map.current || !stats) return;

    // Calculate cumulative distances if not already done
    if (!cumulativeDistancesRef.current) {
      cumulativeDistancesRef.current = calculateCumulativeDistances(gpxData.coordinates);
    }

    // Get start position
    const startPosition = currentDistance > 0
      ? getPositionAtDistance(gpxData.coordinates, cumulativeDistancesRef.current, currentDistance)
      : gpxData.coordinates[0];

    // Create puck marker if it doesn't exist
    if (!puckMarkerRef.current) {
      const puckEl = document.createElement('div');
      puckEl.className = 'gpx-puck';
      puckEl.innerHTML = '<div class="puck-inner"></div><div class="puck-trail"></div>';
      puckMarkerRef.current = new mapboxgl.Marker(puckEl)
        .setLngLat(startPosition)
        .addTo(map.current);
    }

    // Calculate initial bearing for rotation
    const initialBearing = getBearingAtDistance(
      gpxData.coordinates,
      cumulativeDistancesRef.current,
      currentDistance
    );

    // Zoom in, center on the puck, pitch for 3D terrain view, and rotate to look behind
    // Camera looks from behind the puck in the direction of travel
    const cameraBearing = (initialBearing + BEARING_OFFSET) % 360;
    map.current.flyTo({
      center: startPosition,
      zoom: ANIMATION_ZOOM_LEVEL,
      bearing: cameraBearing,
      pitch: CAMERA_PITCH,
      duration: 1000
    });

    // Wait for the flyTo to complete before starting animation
    setTimeout(() => {
      setIsAnimating(true);
      setIsPaused(false);
      setIsScrubMode(false); // Disable scrub mode during animation

      // Initialize currentBearingRef with the initial bearing for smooth scrub transitions later
      currentBearingRef.current = initialBearing;

      const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1];
      let lastTimestamp = null;
      let currentDist = currentDistance; // Start from current position

      const animate = (timestamp) => {
        if (!lastTimestamp) lastTimestamp = timestamp;
        const deltaTime = (timestamp - lastTimestamp) / 1000; // seconds
        lastTimestamp = timestamp;

        // Move at configured km/h (use ref for real-time slider updates)
        const speedMs = (animationSpeedRef.current * 1000) / 3600; // Convert km/h to m/s
        currentDist += speedMs * deltaTime;

        if (currentDist >= totalDistance) {
          // Animation complete
          currentDist = totalDistance;
          setCurrentDistance(currentDist);
          setAnimationProgress(1);

          const finalPos = gpxData.coordinates[gpxData.coordinates.length - 1];
          if (puckMarkerRef.current) {
            puckMarkerRef.current.setLngLat(finalPos);
          }
          // Center on final position (keep the last bearing)
          if (map.current) {
            map.current.panTo(finalPos, { duration: 0 });
          }

          setIsAnimating(false);
          setIsPaused(false);
          animationRef.current = null;
          return;
        }

        // Update position
        const position = getPositionAtDistance(
          gpxData.coordinates,
          cumulativeDistancesRef.current,
          currentDist
        );

        // Calculate target bearing for this position (direction of travel)
        const targetBearing = getBearingAtDistance(
          gpxData.coordinates,
          cumulativeDistancesRef.current,
          currentDist
        );

        // Apply bearing smoothing (same logic as smoothBearing function)
        // This prevents jerky camera rotation in curvy sections
        // Smoothing factor decreases (more smoothing) as speed increases
        // At 100 km/h: factor = 0.08, at 500 km/h: factor = 0.04, at 2000 km/h: factor = 0.02
        const currentSpeed = animationSpeedRef.current;
        const smoothingFactor = Math.max(0.01, 0.08 * (100 / Math.max(100, currentSpeed)));

        let currentBearing = currentBearingRef.current;
        let delta = targetBearing - currentBearing;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        const newBearing = currentBearing + delta * smoothingFactor;
        const normalizedBearing = ((newBearing % 360) + 360) % 360;

        // Add to history buffer for additional smoothing
        bearingHistoryRef.current.push(normalizedBearing);
        if (bearingHistoryRef.current.length > BEARING_HISTORY_SIZE) {
          bearingHistoryRef.current.shift();
        }

        // Calculate weighted average of history
        let weightedSum = 0;
        let weightTotal = 0;
        const history = bearingHistoryRef.current;
        for (let i = 0; i < history.length; i++) {
          const weight = i + 1;
          let histBearing = history[i];
          if (history.length > 1) {
            const refBearing = history[history.length - 1];
            let diff = histBearing - refBearing;
            if (diff > 180) histBearing -= 360;
            if (diff < -180) histBearing += 360;
          }
          weightedSum += histBearing * weight;
          weightTotal += weight;
        }
        const bearing = ((weightedSum / weightTotal % 360) + 360) % 360;
        currentBearingRef.current = bearing;

        if (puckMarkerRef.current) {
          puckMarkerRef.current.setLngLat(position);
        }

        // Use FreeCamera API to set fixed altitude, preventing zoom flickering over terrain
        // Position camera BEHIND the puck and look at the puck
        // Use refs to get latest values (avoids stale closure from slider changes)
        if (map.current) {
          const behindBearing = (bearing + 180) % 360; // Opposite of travel direction
          const cameraPosition = offsetPosition(position, behindBearing, cameraDistanceRef.current);

          const camera = map.current.getFreeCameraOptions();
          camera.position = mapboxgl.MercatorCoordinate.fromLngLat(cameraPosition, cameraAltitudeRef.current);
          camera.lookAtPoint(position); // Look at the puck (puck will be in center of view)
          map.current.setFreeCameraOptions(camera);
        }

        setCurrentDistance(currentDist);
        setAnimationProgress(currentDist / totalDistance);

        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);
    }, 1000);
  }, [gpxData, stats, currentDistance]);

  const pauseAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsPaused(true);
    setIsAnimating(false);
    setIsScrubMode(true);

    // Ensure cumulative distances are calculated for scrubbing
    if (!cumulativeDistancesRef.current && gpxData) {
      cumulativeDistancesRef.current = calculateCumulativeDistances(gpxData.coordinates);
    }

    // Create puck marker if it doesn't exist (needed for scrubbing)
    if (!puckMarkerRef.current && gpxData && map.current) {
      const startPosition = currentDistance > 0 && cumulativeDistancesRef.current
        ? getPositionAtDistance(gpxData.coordinates, cumulativeDistancesRef.current, currentDistance)
        : gpxData.coordinates[0];

      const puckEl = document.createElement('div');
      puckEl.className = 'gpx-puck';
      puckEl.innerHTML = '<div class="puck-inner"></div><div class="puck-trail"></div>';
      puckMarkerRef.current = new mapboxgl.Marker(puckEl)
        .setLngLat(startPosition)
        .addTo(map.current);
    }

    // Disable map's default drag behavior so we can use it for scrubbing
    if (map.current) {
      map.current.dragPan.disable();
    }
  }, [gpxData, currentDistance]);

  const stopAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Reset to start
    setIsAnimating(false);
    setIsPaused(false);
    setAnimationProgress(0);
    setCurrentDistance(0);
    setIsScrubMode(false);

    // Clear bearing history for fresh start
    bearingHistoryRef.current = [];
    currentBearingRef.current = 0;

    // Re-enable map's default drag behavior
    if (map.current) {
      map.current.dragPan.enable();
    }

    // Move puck back to start
    if (puckMarkerRef.current && gpxData) {
      puckMarkerRef.current.setLngLat(gpxData.coordinates[0]);
    }

    // Zoom back out to show the full route, reset bearing to north and pitch to flat
    if (map.current && gpxData) {
      const bounds = new mapboxgl.LngLatBounds();
      gpxData.coordinates.forEach(coord => bounds.extend(coord));
      map.current.fitBounds(bounds, {
        padding: 50,
        duration: 1000,
        bearing: 0, // Reset rotation to north
        pitch: 0 // Reset to flat view
      });
    }
  }, [gpxData]);

  // Cleanup animation on unmount or when GPX changes
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Reset animation when GPX data changes
  useEffect(() => {
    if (gpxData) {
      // Reset animation state
      setIsAnimating(false);
      setIsPaused(false);
      setAnimationProgress(0);
      setCurrentDistance(0);
      setIsScrubMode(false);
      cumulativeDistancesRef.current = null;

      // Clear bearing history for fresh start
      bearingHistoryRef.current = [];
      currentBearingRef.current = 0;

      // Remove old puck marker
      if (puckMarkerRef.current) {
        puckMarkerRef.current.remove();
        puckMarkerRef.current = null;
      }
    }
  }, [gpxData]);

  // Scrub/drag handlers for manual route navigation
  const handleScrubStart = useCallback((clientY) => {
    if (!isScrubMode || !cumulativeDistancesRef.current) return;

    isDraggingRef.current = true;
    dragStartYRef.current = clientY;
    dragStartDistanceRef.current = currentDistance;
  }, [isScrubMode, currentDistance]);

  const handleScrubMove = useCallback((clientY) => {
    if (!isDraggingRef.current || !cumulativeDistancesRef.current) return;

    // Calculate vertical movement (positive = dragging down = move forward on route)
    // This makes it feel like you're "pulling" the map under the puck
    const deltaY = clientY - dragStartYRef.current;

    // Convert pixels to meters along route
    const deltaDistance = deltaY * DRAG_METERS_PER_PIXEL;
    const newDistance = dragStartDistanceRef.current + deltaDistance;

    updatePositionAtDistance(newDistance);
  }, [updatePositionAtDistance]);

  const handleScrubEnd = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // Mouse event handlers for scrubbing
  const handleMouseDown = useCallback((e) => {
    if (!isScrubMode) return;
    e.preventDefault();
    handleScrubStart(e.clientY);
  }, [isScrubMode, handleScrubStart]);

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    handleScrubMove(e.clientY);
  }, [handleScrubMove]);

  const handleMouseUp = useCallback(() => {
    handleScrubEnd();
  }, [handleScrubEnd]);

  // Touch event handlers for scrubbing
  const handleTouchStart = useCallback((e) => {
    if (!isScrubMode) return;
    const touch = e.touches[0];
    handleScrubStart(touch.clientY);
  }, [isScrubMode, handleScrubStart]);

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    handleScrubMove(touch.clientY);
  }, [handleScrubMove]);

  const handleTouchEnd = useCallback(() => {
    handleScrubEnd();
  }, [handleScrubEnd]);

  // Attach global mouse/touch event listeners for scrub mode
  useEffect(() => {
    if (!isScrubMode) return;

    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();
    const handleGlobalTouchMove = (e) => {
      if (isDraggingRef.current) {
        e.preventDefault();
        const touch = e.touches[0];
        handleScrubMove(touch.clientY);
      }
    };
    const handleGlobalTouchEnd = () => handleTouchEnd();

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    window.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isScrubMode, handleMouseMove, handleMouseUp, handleScrubMove, handleTouchEnd]);

  // Keyboard event handler for arrow key scrubbing with smooth continuous movement
  // Meters to move per second when holding arrow key
  const ARROW_KEY_SPEED = DRAG_METERS_PER_PIXEL * 60; // 60 pixels worth per second
  const arrowKeyDirectionRef = useRef(0); // -1, 0, or 1
  const arrowKeyAnimationRef = useRef(null);
  const lastArrowKeyTimeRef = useRef(null);
  const arrowKeyDistanceRef = useRef(0); // Track distance in ref to avoid stale closure

  // Keep the ref in sync with state
  useEffect(() => {
    arrowKeyDistanceRef.current = currentDistance;
  }, [currentDistance]);

  useEffect(() => {
    if (!isScrubMode) {
      // Clean up if scrub mode is disabled while holding key
      if (arrowKeyAnimationRef.current) {
        cancelAnimationFrame(arrowKeyAnimationRef.current);
        arrowKeyAnimationRef.current = null;
      }
      arrowKeyDirectionRef.current = 0;
      return;
    }

    const animateArrowKey = (timestamp) => {
      if (arrowKeyDirectionRef.current === 0) {
        arrowKeyAnimationRef.current = null;
        lastArrowKeyTimeRef.current = null;
        return;
      }

      if (!lastArrowKeyTimeRef.current) {
        lastArrowKeyTimeRef.current = timestamp;
      }

      const deltaTime = (timestamp - lastArrowKeyTimeRef.current) / 1000; // seconds
      lastArrowKeyTimeRef.current = timestamp;

      // Move based on direction and elapsed time
      const deltaDistance = arrowKeyDirectionRef.current * ARROW_KEY_SPEED * deltaTime;
      arrowKeyDistanceRef.current = arrowKeyDistanceRef.current + deltaDistance;
      updatePositionAtDistance(arrowKeyDistanceRef.current);

      arrowKeyAnimationRef.current = requestAnimationFrame(animateArrowKey);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();

        // ArrowDown = move forward (like dragging down), ArrowUp = move backward
        const direction = e.key === 'ArrowDown' ? 1 : -1;

        // Only start animation if direction changed or not already animating
        if (arrowKeyDirectionRef.current !== direction) {
          arrowKeyDirectionRef.current = direction;
          lastArrowKeyTimeRef.current = null;

          if (!arrowKeyAnimationRef.current) {
            arrowKeyAnimationRef.current = requestAnimationFrame(animateArrowKey);
          }
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // Stop if releasing the key that's currently active
        const releasedDirection = e.key === 'ArrowDown' ? 1 : -1;
        if (arrowKeyDirectionRef.current === releasedDirection) {
          arrowKeyDirectionRef.current = 0;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (arrowKeyAnimationRef.current) {
        cancelAnimationFrame(arrowKeyAnimationRef.current);
        arrowKeyAnimationRef.current = null;
      }
    };
  }, [isScrubMode, updatePositionAtDistance]);

  // Format distance for display
  const formatAnimationDistance = (meters) => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(2)} km`;
  };

  // Calculate estimated time at current animation speed
  const formatEstimatedTime = (meters) => {
    const hours = meters / (animationSpeedKmh * 1000);
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h > 0) {
      return `${h}h ${m}m`;
    }
    return `${m}m`;
  };

  // Don't render anything if not admin (will redirect)
  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="gpx-viewer-page">
      <div className="gpx-viewer-container">
        <div className="gpx-viewer-header">
          <button className="back-button" onClick={handleBack}>
            <IconArrowLeft size={20} />
            Back
          </button>
          <div className="header-content">
            <div className="header-icon">
              <IconRoute size={32} />
            </div>
            <div>
              <h1>GPX Viewer</h1>
              <p className="gpx-viewer-subtitle">View and analyze GPX route files</p>
            </div>
          </div>
        </div>

        <div className="gpx-upload-section">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".gpx"
            style={{ display: 'none' }}
          />
          <button
            className="upload-button"
            onClick={() => fileInputRef.current?.click()}
          >
            <IconUpload size={20} />
            Upload GPX File
          </button>
          {fileName && (
            <span className="current-file">
              <IconMap size={16} />
              {fileName}
            </span>
          )}
        </div>

        {error && (
          <div className="gpx-error">
            {error}
          </div>
        )}

        {stats && (
          <div className="gpx-stats">
            <div className="stat-card">
              <IconRuler size={20} />
              <div className="stat-content">
                <span className="stat-value">{stats.distance} km</span>
                <span className="stat-label">Distance</span>
              </div>
            </div>
            <div className="stat-card">
              <IconMountain size={20} />
              <div className="stat-content">
                <span className="stat-value">{stats.elevationGain} m</span>
                <span className="stat-label">Elevation Gain</span>
              </div>
            </div>
            <div className="stat-card">
              <IconMountain size={20} style={{ transform: 'rotate(180deg)' }} />
              <div className="stat-content">
                <span className="stat-value">{stats.elevationLoss} m</span>
                <span className="stat-label">Elevation Loss</span>
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-icon-text">↑</span>
              <div className="stat-content">
                <span className="stat-value">{stats.maxElevation} m</span>
                <span className="stat-label">Max Elevation</span>
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-icon-text">↓</span>
              <div className="stat-content">
                <span className="stat-value">{stats.minElevation} m</span>
                <span className="stat-label">Min Elevation</span>
              </div>
            </div>
            {gpxData && (
              <div className="stat-card">
                <IconRoute size={20} />
                <div className="stat-content">
                  <span className="stat-value">{gpxData.pointCount.toLocaleString()}</span>
                  <span className="stat-label">Track Points</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="gpx-map-container" style={{ width: `${mapWidth}%` }}>
          {isLoading && (
            <div className="map-loading">
              <div className="loading-spinner"></div>
              <span>Loading map...</span>
            </div>
          )}
          <div
            ref={mapContainer}
            className={`gpx-map ${isScrubMode ? 'scrub-mode' : ''}`}
            style={{ height: `${mapHeight}px` }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          />

          {/* Map style toggle button - shows current style, click to switch */}
          <button
            className="map-style-toggle"
            onClick={cycleMapStyle}
            title={`Current: ${MAP_STYLES[mapStyleIndex].name}. Click to switch to ${MAP_STYLES[(mapStyleIndex + 1) % MAP_STYLES.length].name}`}
          >
            {mapStyleIndex === 2 ? <IconTrees size={20} /> : mapStyleIndex === 1 ? <IconSatellite size={20} /> : <IconMap size={20} />}
            <span>{MAP_STYLES[mapStyleIndex].name}</span>
          </button>

          {/* Overhead/3D view toggle button */}
          <button
            className="view-toggle-button"
            onClick={toggleOverheadView}
            title={isOverheadView ? 'Switch to 3D view' : 'Switch to overhead view'}
          >
            {isOverheadView ? <Icon3dCubeSphere size={20} /> : <IconEye size={20} />}
            <span>{isOverheadView ? '3D View' : 'Overhead'}</span>
          </button>

          {/* Zoom level display */}
          <div className="zoom-level-display">
            Zoom: {zoomLevel.toFixed(1)}
          </div>

          {/* Map dimension controls */}
          <div className="map-dimension-settings">
            <div className="camera-setting">
              <label>Width: {mapWidth}%</label>
              <input
                type="range"
                min="50"
                max="100"
                step="5"
                value={mapWidth}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setMapWidth(value);
                  localStorage.setItem('gpxViewer_mapWidth', value.toString());
                }}
              />
            </div>
            <div className="camera-setting">
              <label>Height: {mapHeight}px</label>
              <input
                type="range"
                min="300"
                max="1200"
                step="50"
                value={mapHeight}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setMapHeight(value);
                  localStorage.setItem('gpxViewer_mapHeight', value.toString());
                }}
              />
            </div>
          </div>

          {/* Camera settings (only visible during animation/scrub mode) */}
          {(isAnimating || isPaused || isScrubMode) && (
            <div className="camera-settings">
              <div className="camera-setting">
                <label>Speed: {animationSpeedKmh} km/h</label>
                <input
                  type="range"
                  min="10"
                  max="5000"
                  step="10"
                  value={animationSpeedKmh}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setAnimationSpeedKmh(value);
                    animationSpeedRef.current = value;
                    localStorage.setItem('gpxViewer_animationSpeed', value.toString());
                  }}
                />
              </div>
              <div className="camera-setting">
                <label>Altitude: {cameraAltitude}m</label>
                <input
                  type="range"
                  min="100"
                  max="10000"
                  step="100"
                  value={cameraAltitude}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setCameraAltitude(value);
                    cameraAltitudeRef.current = value;
                    localStorage.setItem('gpxViewer_cameraAltitude', value.toString());
                  }}
                />
              </div>
              <div className="camera-setting">
                <label>Distance: {cameraDistance}m</label>
                <input
                  type="range"
                  min="100"
                  max="20000"
                  step="100"
                  value={cameraDistance}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setCameraDistance(value);
                    cameraDistanceRef.current = value;
                    localStorage.setItem('gpxViewer_cameraDistance', value.toString());
                  }}
                />
              </div>
            </div>
          )}

          {/* Tile processing controls */}
          {gpxData && (
            <div className="tile-processing-controls">
              <div className="tile-process-buttons">
                <button
                  className="tile-process-button"
                  onClick={handleProcessTiles}
                  disabled={isProcessingTiles || !customStyleName.trim()}
                  title="Process route tiles with Gemini AI"
                >
                  <IconPhoto size={20} />
                  {isProcessingTiles ? (
                    <span>Processing... {processingProgress}%</span>
                  ) : Object.keys(processedTiles).length > 0 ? (
                    <span>Reprocess ({Object.keys(processedTiles).length} tiles)</span>
                  ) : (
                    <span>Process Tiles</span>
                  )}
                </button>
                {isProcessingTiles && processAllTiles && (
                  <button
                    className="tile-stop-button"
                    onClick={handleStopProcessing}
                    disabled={stopRequested}
                    title="Stop processing after current tile finishes"
                  >
                    {stopRequested ? 'Stopping...' : 'Stop'}
                  </button>
                )}
              </div>
              {customStyles.length > 0 && (
                <div className="tile-options">
                  <select
                    className="style-selector"
                    value={processingStyle}
                    onChange={(e) => setProcessingStyle(e.target.value)}
                    disabled={isProcessingTiles}
                    title="Select saved style"
                  >
                    <option value="gemini-3-pro">New Style</option>
                    {customStyles.map(style => (
                      <option key={style} value={style}>{style}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Animation controls overlay */}
          {gpxData && stats && (
            <div className="animation-controls">
              <div className="animation-buttons">
                {!isAnimating && !isPaused && (
                  <button className="anim-btn play" onClick={startAnimation} title="Start animation">
                    <IconPlayerPlay size={20} />
                  </button>
                )}
                {isAnimating && (
                  <button className="anim-btn pause" onClick={pauseAnimation} title="Pause animation">
                    <IconPlayerPause size={20} />
                  </button>
                )}
                {isPaused && (
                  <button className="anim-btn play" onClick={startAnimation} title="Resume animation">
                    <IconPlayerPlay size={20} />
                  </button>
                )}
                {(isAnimating || isPaused || animationProgress > 0) && (
                  <button className="anim-btn stop" onClick={stopAnimation} title="Stop and reset">
                    <IconPlayerStop size={20} />
                  </button>
                )}
              </div>

              {(isAnimating || isPaused || animationProgress > 0) && (
                <div className="animation-info">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${animationProgress * 100}%` }}
                    />
                  </div>
                  <div className="animation-stats">
                    <span>{formatAnimationDistance(currentDistance)} / {stats.distance} km</span>
                    {isPaused ? (
                      <span className="scrub-hint">↕ Drag or use arrow keys</span>
                    ) : (
                      <span className="animation-speed">@ {animationSpeedKmh} km/h</span>
                    )}
                  </div>
                </div>
              )}

              {!isAnimating && !isPaused && animationProgress === 0 && (
                <div className="animation-hint">
                  <span>Estimated time: {formatEstimatedTime(stats.distanceMeters)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Gemini processing options - below map */}
        {gpxData && (
          <div className="gemini-options-section">
            <div className="gemini-options-row">
              <input
                type="text"
                className="gemini-style-input"
                placeholder="Style name (required)"
                value={customStyleName}
                onChange={(e) => setCustomStyleName(e.target.value)}
                disabled={isProcessingTiles}
                title="Name for this style (used for caching)"
              />
              <label className="process-all-toggle" title="Process all uncached tiles at once (slower but complete)">
                <input
                  type="checkbox"
                  checked={processAllTiles}
                  onChange={(e) => setProcessAllTiles(e.target.checked)}
                  disabled={isProcessingTiles}
                />
                <span>Process All Tiles</span>
              </label>
              <label className="process-all-toggle" title="Raw mode: Send only the map tile and prompt directly to Gemini (no text overlays, neighbors, or pre-prompt)">
                <input
                  type="checkbox"
                  checked={rawMode}
                  onChange={(e) => {
                  setRawMode(e.target.checked);
                  localStorage.setItem('gpxViewer_rawMode', e.target.checked.toString());
                }}
                  disabled={isProcessingTiles}
                />
                <span>Raw Mode</span>
              </label>
            </div>
            {/* Reference photo upload */}
            <div className="reference-photo-section">
              <input
                type="file"
                ref={referencePhotoInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      setReferencePhoto(event.target.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <button
                className="reference-photo-button"
                onClick={() => referencePhotoInputRef.current?.click()}
                disabled={isProcessingTiles}
                title="Upload a reference photo to send as the first image to Gemini"
              >
                {referencePhoto ? '📷 Change Reference Photo' : '📷 Add Reference Photo'}
              </button>
              {referencePhoto && (
                <div className="reference-photo-preview">
                  <img src={referencePhoto} alt="Reference" className="reference-photo-thumb" />
                  <button
                    className="reference-photo-remove"
                    onClick={() => setReferencePhoto(null)}
                    disabled={isProcessingTiles}
                    title="Remove reference photo"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
            <textarea
              className="gemini-prompt-input"
              placeholder="Enter prompt for Gemini..."
              value={geminiPrompt}
              onChange={(e) => {
                setGeminiPrompt(e.target.value);
                // Auto-resize textarea
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              disabled={isProcessingTiles}
              title="Prompt for Gemini-3-Pro image transformation"
            />
          </div>
        )}

        {/* Processed tiles list */}
        {Object.keys(processedTiles).length > 0 && (
          <div className="processed-tiles-section">
            <div className="processed-tiles-header">
              <h4>Processed Tiles ({Object.keys(processedTiles).length})</h4>
              <span className="tiles-style-name">{customStyleName || processingStyle}</span>
            </div>
            <div className="processed-tiles-grid">
              {Object.entries(processedTiles)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([tileKey, url]) => (
                  <div key={tileKey} className="processed-tile-item">
                    <img
                      src={url}
                      alt={`Tile ${tileKey}`}
                      className="processed-tile-thumb"
                      onClick={() => setLightboxTile({ key: tileKey, url })}
                      style={{ cursor: 'pointer' }}
                    />
                    <span className="processed-tile-key">{tileKey}</span>
                    <button
                      className="processed-tile-delete"
                      onClick={() => deleteTile(tileKey)}
                      disabled={deletingTile === tileKey}
                      title="Delete this tile"
                    >
                      {deletingTile === tileKey ? '...' : '×'}
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Debug panel for Gemini generation visualization */}
        {debugInfo && (
          <div className="gemini-debug-panel" key={`debug-${debugInfo.timestamp}`}>
            <h4>Last Generation Debug - Tile {debugInfo.tileCoords}</h4>

            <div className="debug-images-row">
              {/* Reference photo FIRST (if uploaded) */}
              {debugInfo.referencePhoto && (
                <div className="debug-image-container">
                  <div className="debug-image-label">Image 1: REFERENCE PHOTO</div>
                  <img src={debugInfo.referencePhoto} alt="Uploaded reference photo" className="debug-image" />
                </div>
              )}

              {/* Reference tiles (with "REFERENCE STYLE" overlay) - in order sent to Gemini */}
              {debugInfo.neighborTiles.map((neighbor, idx) => (
                <div key={idx} className="debug-image-container">
                  <div className="debug-image-label">Image {(debugInfo.referencePhoto ? 2 : 1) + idx}: {neighbor.position.toUpperCase()} (Reference)</div>
                  <img src={neighbor.dataUrl} alt={`Style reference ${neighbor.position}`} className="debug-image" />
                </div>
              ))}

              {/* Map tile - as sent to Gemini */}
              <div className="debug-image-container">
                <div className="debug-image-label">Image {(debugInfo.referencePhoto ? 2 : 1) + debugInfo.neighborTiles.length}: INPUT MAP</div>
                <img src={debugInfo.inputUrl} alt="Map image to transform" className="debug-image" />
              </div>

              {/* Arrow */}
              <div className="debug-arrow">→</div>

              {/* Output tile */}
              <div className="debug-image-container">
                <div className="debug-image-label">OUTPUT (Generated)</div>
                <img src={debugInfo.outputUrl} alt="Generated output tile" className="debug-image" />
              </div>
            </div>

            <div className="debug-prompt-section">
              <div className="debug-prompt-label">Constructed Prompt:</div>
              <pre className="debug-prompt-text">{debugInfo.constructedPrompt}</pre>
            </div>

            <div className="debug-prompt-section" style={{ marginTop: '12px' }}>
              <div className="debug-prompt-label">Gemini Response Text:</div>
              <pre className="debug-prompt-text">{debugInfo.geminiText || '(No text response from Gemini)'}</pre>
            </div>

            {debugInfo.thoughtsSummary && (
              <div className="debug-prompt-section" style={{ marginTop: '12px' }}>
                <div className="debug-prompt-label">Gemini Thought Summary:</div>
                <pre className="debug-prompt-text debug-thoughts">{debugInfo.thoughtsSummary}</pre>
              </div>
            )}
          </div>
        )}

        <div className="gpx-instructions">
          <h3>How to use</h3>
          <ul>
            <li>Upload a GPX file using the button above, or view the default sample route</li>
            <li>Use mouse/touch to pan and zoom the map</li>
            <li>Press play to animate along the route (adjust speed with the slider)</li>
            <li>When paused, drag up/down on the map or use arrow keys to scrub through the route</li>
            <li>Green marker (S) = Start, Red marker (F) = Finish</li>
          </ul>
        </div>
      </div>

      {/* Lightbox modal for viewing larger tile images */}
      {lightboxTile && (
        <div
          className="tile-lightbox-overlay"
          onClick={() => setLightboxTile(null)}
        >
          <div className="tile-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="tile-lightbox-close"
              onClick={() => setLightboxTile(null)}
              title="Close (Escape)"
            >
              ×
            </button>
            <img
              src={lightboxTile.url}
              alt={`Tile ${lightboxTile.key}`}
              className="tile-lightbox-image"
            />
            <div className="tile-lightbox-label">{lightboxTile.key}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GpxViewerPage;
